import type { NextPage, GetStaticProps } from 'next';
import { useScreenViewEvent } from '@stitch-fix/event-reporter';
import Layout from '../../../../../components/Layout';
import PaginatedRecommendations from '../../../../../components/PaginatedRecommendations';
import { CategoryCardFragment } from '../../../../../queries/getCategorySetPageAtBuildTime';
import PageHeader from '../../../../../components/PageHeader';
import { getCategorySetPageByBusinessLine, formatCategoryPaths } from '../../../../../queries/getCategorySetPage';
import { BusinessLineRoutes, routeToBusinessLine, routeToBusinessLineDisplay } from '../../../../../utils/routingHelper';
import { BreadcrumbsProps } from '../../../../../components/Breadcrumbs';
import MoreCategoriesModule, { MoreCategoriesModuleProps } from '../../../../../components/MoreCategoriesModule';
import { CATEGORY_SET_IDS } from '../constants';
import { useGetCategoryPageQuery } from './getCategoryPage';
import { formatMoreCategoriesModule } from '../../../../../components/MoreCategoriesModule/utils';

/**
 * Category Listing Page by Category Id
 * This page renders a Category Listing page for the ungated Categories experience.
 *
 * All category pages are statically generated with initial container "shell" data
 * including (seo, page title, fix promos).
 *
 * Any dynamic data is fetch client side via graphql and rendered on the page
 */

export const getStaticPaths = async () => {
  // Get cached categoriesById
  const categorySetPages = await Promise.all(CATEGORY_SET_IDS.map(categorySetId => [getCategorySetPageByBusinessLine('WOMENS', categorySetId), getCategorySetPageByBusinessLine('MENS', categorySetId)]).flat());
  const paths = categorySetPages.map(categorySetPage => categorySetPage ? formatCategoryPaths(categorySetPage) : []).flat();

  // Setting `fallback` to false to properly 404 on any categories not statically generated
  return {
    paths,
    fallback: false
  };
};
interface PageProps {
  breadcrumbs: BreadcrumbsProps['breadcrumbs'];
  businessLine: BusinessLineRoutes;
  category: CategoryCardFragment['category'] | null | undefined;
  moreCategories: MoreCategoriesModuleProps | null | undefined;
}
export const getStaticProps: GetStaticProps<PageProps, {
  businessLine: BusinessLineRoutes;
  categorySetId: string;
  categoryId: string;
}> = async context => {
  // freestyle isn't available in the UK
  if (context.locale === 'en-GB') {
    return {
      notFound: true
    };
  }
  const {
    businessLine = 'women',
    categorySetId = '',
    categoryId
  } = context.params || {};

  // Get cached data from previous graph query in `getStaticPaths`
  const cachedData = await getCategorySetPageByBusinessLine(routeToBusinessLine[businessLine], categorySetId);
  const categoryCard = cachedData?.categorySet.cards.find(({
    category: {
      id
    }
  }) => categoryId === id);
  const categorySetUrl = `/${businessLine}/clothing/${categorySetId}`;
  const breadcrumbs: BreadcrumbsProps['breadcrumbs'] = [{
    text: routeToBusinessLineDisplay(businessLine),
    href: `/${businessLine}`
  }, {
    text: cachedData?.title || '',
    href: categorySetUrl
  }, {
    text: categoryCard?.category.pageTitle || ''
  }];
  const fallbackMoreCategoriesCategorySetPage = await getCategorySetPageByBusinessLine(routeToBusinessLine[businessLine], 'departments');
  const moreCategories: MoreCategoriesModuleProps | null = formatMoreCategoriesModule({
    businessLine,
    categoryId,
    categorySetPage: cachedData,
    fallbackCategorySetPage: fallbackMoreCategoriesCategorySetPage
  });
  return {
    props: {
      breadcrumbs,
      businessLine,
      category: categoryCard?.category,
      moreCategories
    }
  };
};
const CategoriesListingPage: NextPage<PageProps> = ({
  breadcrumbs,
  businessLine,
  category,
  moreCategories
}) => {
  // v2 page view event
  const {
    EventProvider
  } = useScreenViewEvent({
    schema: 'category_screen_view',
    category_id: category?.id || '',
    name: 'category',
    source_app: 'categories-next-ui'
  });
  const {
    data
  } = useGetCategoryPageQuery({
    variables: {
      businessLine: routeToBusinessLine[businessLine],
      categoryId: category?.id || ''
    },
    fetchPolicy: 'cache-only'
  });
  const categoryPage = data?.shopContent?.categoryPage;
  return <EventProvider>
      <Layout title={category?.pageTitle} breadcrumbs={breadcrumbs} hero={{
      businessLine,
      variant: 'categoryPage'
    }}>
        <PageHeader handle={categoryPage?.handle} image={categoryPage?.image} subTitle={categoryPage?.description || 'Shop our most popular selections'} title={category?.pageTitle} />

        {category && <PaginatedRecommendations businessLine={category.businessLine} categoryId={category.id} categorySetId={category.categorySetId} />}

        {moreCategories && <MoreCategoriesModule {...moreCategories} />}
      </Layout>
    </EventProvider>;
};
export default CategoriesListingPage;